import { useEffect, useRef, useState } from "react";
import { promoteManufacturingMaterialLinesToUse } from "@/lib/manufacturing/materialUsesClient";

/** Dry-run promote for each material; returns total BOM lines still pinned. */
export async function countConvertibleBomLines(
  materialIds: readonly string[],
  materialUseId: string,
): Promise<{ total: number; byMaterialId: Record<string, number> }> {
  const byMaterialId: Record<string, number> = {};
  let total = 0;
  await Promise.all(
    materialIds.map(async (materialId) => {
      const r = await promoteManufacturingMaterialLinesToUse({
        from_material_id: materialId,
        material_use_id: materialUseId,
        dry_run: true,
      });
      const lines = r.ok ? r.data.lines : 0;
      byMaterialId[materialId] = lines;
      total += lines;
    }),
  );
  return { total, byMaterialId };
}

export function useMaterialUseConvertPending(
  materialUseId: string | undefined,
  memberMaterialIds: readonly string[],
  onPendingConvertChange?: (pendingLines: number) => void,
) {
  const [pendingTotal, setPendingTotal] = useState(0);
  const [byMaterialId, setByMaterialId] = useState<Record<string, number>>({});
  const [scanBusy, setScanBusy] = useState(false);
  const onChangeRef = useRef(onPendingConvertChange);
  onChangeRef.current = onPendingConvertChange;

  const idsKey = memberMaterialIds.slice().sort().join(",");

  useEffect(() => {
    if (!materialUseId || memberMaterialIds.length === 0) {
      setPendingTotal(0);
      setByMaterialId({});
      onChangeRef.current?.(0);
      return;
    }
    let cancelled = false;
    setScanBusy(true);
    void countConvertibleBomLines(memberMaterialIds, materialUseId).then((result) => {
      if (cancelled) return;
      setScanBusy(false);
      setPendingTotal(result.total);
      setByMaterialId(result.byMaterialId);
      onChangeRef.current?.(result.total);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idsKey captures member ids
  }, [materialUseId, idsKey]);

  async function rescan() {
    if (!materialUseId || memberMaterialIds.length === 0) {
      setPendingTotal(0);
      setByMaterialId({});
      onChangeRef.current?.(0);
      return;
    }
    setScanBusy(true);
    const result = await countConvertibleBomLines(memberMaterialIds, materialUseId);
    setScanBusy(false);
    setPendingTotal(result.total);
    setByMaterialId(result.byMaterialId);
    onChangeRef.current?.(result.total);
  }

  return { pendingTotal, byMaterialId, scanBusy, rescan };
}
